import React, {Component, createRef} from "react"
import { Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import DOMPurify from 'dompurify'
import {store} from "../../App";
import { setDialog, clearDialog } from "../../Redux/OverlayActions";
import { withTranslation } from "../../hooks/withTranslation";
import { scoopMarkerHTML, scoopVariantForKar } from './ScoopMarker';
import { statusToText as sharedStatusToText, statusPillForKar } from './truckStatus';

class TruckMarker extends Component {
    constructor() {
        super();
        this.markerRef = createRef();
        this.animationFrame = null;
        this.statusToText = this.statusToText.bind(this);
        this.openRouteWithDisclaimer = this.openRouteWithDisclaimer.bind(this);
    }

    componentDidMount() {
    }

    // Prevent unnecessary re-renders during polling - only re-render if relevant props changed
    shouldComponentUpdate(nextProps) {
        const kar = this.props.kar;
        const nextKar = nextProps.kar;

        // Re-render if position changed (for animation)
        if (this.props.lat !== nextProps.lat || this.props.lng !== nextProps.lng) {
            return true;
        }
        // Re-render if availability or status changed
        if (kar.available !== nextKar.available || kar.status !== nextKar.status) {
            return true;
        }
        // Re-render if trackid changed (for tooltip state)
        if (this.props.trackid !== nextProps.trackid) {
            return true;
        }
        return false;
    }

    componentDidUpdate(prevProps) {
        // Animate marker when position changes
        if (prevProps.lat !== this.props.lat || prevProps.lng !== this.props.lng) {
            this.animateMarker(
                [prevProps.lat, prevProps.lng],
                [this.props.lat, this.props.lng]
            );
        }
    }

    componentWillUnmount() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
    }

    animateMarker(fromLatLng, toLatLng) {
        const marker = this.markerRef.current;
        if (!marker) return;

        const duration = 1000; // 1 second animation
        const startTime = performance.now();
        const startLat = fromLatLng[0];
        const startLng = fromLatLng[1];
        const deltaLat = toLatLng[0] - startLat;
        const deltaLng = toLatLng[1] - startLng;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Ease-out cubic for smooth deceleration
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            const newLat = startLat + (deltaLat * easeProgress);
            const newLng = startLng + (deltaLng * easeProgress);

            marker.setLatLng([newLat, newLng]);

            if (progress < 1) {
                this.animationFrame = requestAnimationFrame(animate);
            }
        };

        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        this.animationFrame = requestAnimationFrame(animate);
    }

    openRouteWithDisclaimer(lat, lng) {
        const kar = this.props.kar;
        const { t } = this.props;
        const messageTemplate = t('tracker.truckMarker.routeDisclaimer.message', '<strong>Let op:</strong> IJskar {title} kan zich intussen verplaatsen. Check regelmatig de app om te zien waar de ijskar zich bevindt.');
        store.dispatch(setDialog({
            title: t('tracker.truckMarker.routeDisclaimer.title', 'Route plannen'),
            message: messageTemplate.replace('{title}', kar.title),
            theme: "warning",
            btn1Text: t('tracker.truckMarker.routeDisclaimer.openButton', 'Open routeplanner'),
            btn1Theme: "primary",
            btn1Title: "open",
            btn2Text: t('common.cancel', 'Annuleer'),
            btn2Title: "cancel",
            response: (response) => {
                store.dispatch(clearDialog());
                if (response === "open") {
                    // Open Google Maps with directions
                    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                    window.open(mapsUrl, '_blank');
                }
            },
        }));
    }

    statusToText(kar) {
        return sharedStatusToText(kar, this.props.t);
    }

    render() {
        const kar = this.props.kar;
        const { t } = this.props;

        // Brand scoop-on-cone divIcon: two-tint sphere (colour = status) with
        // the truck number, on a tan cone triangle whose tip marks the spot.
        const truckNumber = (kar.title && kar.title.match(/^#(\d+)/)) ? kar.title.match(/^#(\d+)/)[1] : kar.id;
        const pill = statusPillForKar(kar, t);

        // Truck marker is per-tenant: the default is the code-drawn scoop-on-cone
        // (recoloured by the brand tokens); a tenant can instead ship its own PNG
        // icons by setting REACT_APP_TRUCK_MARKER_STYLE=image (e.g. Joris IJs uses
        // photo-style truck icons). Image paths live under the tenant's assets.
        let myIcon;
        if ((process.env.REACT_APP_TRUCK_MARKER_STYLE || 'scoop') === 'image') {
            // Photo-style truck icon (e.g. Joris IJs). The photo alone doesn't
            // read as available/busy, so overlay a coloured status dot (same
            // semantic colours as the scoop variants).
            const base = process.env.REACT_APP_TRUCK_ICON_BASE || ((process.env.PUBLIC_URL || '') + '/images/tracker/');
            const dotColor = { green: '#55A86A', red: '#F4303C', tan: '#C79A63', scheduled: '#8C7359' }[scoopVariantForKar(kar)] || '#F4303C';
            const url = base + (kar.available ? 'truck_available.png' : 'truck_unavailable.png');
            myIcon = L.divIcon({
                className: 'truck-marker-icon truck-image-icon',
                html: `<div class="truck-img-wrap"><img class="truck-img" src="${url}" alt="" /><span class="truck-status-dot" style="background:${dotColor}"></span></div>`,
                iconSize: [48, 48],
                iconAnchor: [24, 46],
                popupAnchor: [0, -42],
            });
        } else {
            myIcon = L.divIcon({
                className: 'truck-marker-icon scoop-divicon',
                html: scoopMarkerHTML(scoopVariantForKar(kar), truckNumber, { size: 40, withCone: true }),
                iconSize: [40, 65],
                iconAnchor: [20, 65],
                popupAnchor: [0, -56],
            });
        }

        return (
            <Marker
                ref={this.markerRef}
                icon={myIcon}
                key={"karmarker"+this.props.kar.id}
                zIndexOffset={1000}
                eventHandlers={{
                    click: () => {this.props.onClick(this.props.trackid, kar.latitude, kar.longitude);}
                }}
                position={[this.props.lat, this.props.lng]}
                >
                <Popup className="truck-popup">
                    <div className="truck-popup-content">
                        <h6 className="truck-popup-title">{t('tracker.truckMarker.popup.title', 'IJskar {title}').replace('{title}', kar.title)}</h6>
                        {kar.subtitle ? <p className="truck-popup-subtitle" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(kar.subtitle, { ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br'] })}}></p> : null}
                        <div className="truck-popup-status">
                            <span className={`status-badge pill-${pill.variant}`}>
                                {pill.label}
                            </span>
                            <span className="status-text">{this.statusToText(kar)}</span>
                        </div>
                        <button
                            type="button"
                            className="truck-popup-route-btn"
                            onClick={() => this.openRouteWithDisclaimer(kar.latitude, kar.longitude)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            {t('tracker.truckMarker.popup.routeButton', 'Route plannen')}
                        </button>
                    </div>
                </Popup>
            </Marker>
        )
    }
}

export default withTranslation(TruckMarker)
